import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from '/src/components';
import { useParticipationStore } from '/src/context';
import { Note, Participation } from '/src/lib/models';
import { TListParticipationsRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type ParticipationsListProps = {
  /**
   * List of Participations to display
   */
  data?: Participation[];
  /**
   * Query to include when fetching Participations
   */
  query?: TListParticipationsRequest;
};

/**
 * Participations List
 */
export const ParticipationsList: React.FC<ParticipationsListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const participationStore = useParticipationStore();

  /** State **/
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (participations) {
      const list = participations.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [participations, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Participation[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.currentBalance = 0;
    totals.feesDue = 0;
    totals.interestDue = 0;

    const participations = data.map((participation) => {
      // Calculate totals
      totals.currentBalance += participation.relativeBalance;
      totals.feesDue += participation.loan.feeBalance * participation.participationPercentage;
      totals.interestDue += participation.loan.interestBalance * participation.participationPercentage;

      // Store
      return participation;
    });

    // Save data
    setParticipations(participations);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      participationStore.query(query).then((participations) => participations && formatData(participations));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { propName: 'loan.name', label: 'Deal Name' },
            { propName: 'participationPercentage', label: 'Participation Percentage', textAlign: 'right' },
            { propName: 'currentBalance', label: 'Current Balance', textAlign: 'right' },
            { propName: 'feesDue', label: 'Fees Due', textAlign: 'right' },
            { propName: 'interestDue', label: 'Interest Due', textAlign: 'right' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((participation, i) => (
            <ListRow
              key={`participation-${i}`}
              padForScrollbar={listData.length > 8}
              items={[
                { propName: 'loan.name', content: participation.loan.name, url: `/deals/${participation.loan._id}` },
                {
                  propName: 'participationPercentage',
                  content: utils.percent.format(participation.participationPercentage),
                  textAlign: 'right',
                },
                {
                  propName: 'currentBalance',
                  content: utils.currency.format(participation.relativeBalance),
                  textAlign: 'right',
                },
                {
                  propName: 'feesDue',
                  content: utils.currency.format(participation.loan.feeBalance * participation.participationPercentage),
                  textAlign: 'right',
                },
                {
                  propName: 'interestDue',
                  content: utils.currency.format(
                    participation.loan.interestBalance * participation.participationPercentage
                  ),
                  textAlign: 'right',
                },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { propName: 'loanName', label: 'Deal Name' },
              { propName: 'participationPercentage', label: 'Participation Percentage', textAlign: 'right' },
              { propName: 'currentBalance', label: 'Current Balance', textAlign: 'right' },
              { propName: 'feesDue', label: 'Fees Due', textAlign: 'right' },
              { propName: 'interestDue', label: 'Interest Due', textAlign: 'right' },
            ].map(({ propName, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={
                  i === 0
                    ? 'Totals'
                    : typeof totals[propName] === 'number'
                    ? utils.currency.format(totals[propName])
                    : ''
                }
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Participations found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
